import { SelectOption } from '@factorialco/deprecated-design-system'
import { DAY_TYPES } from 'config/Vars'
import type { Field } from 'factorial-form'
import isEmpty from 'lodash/isEmpty'
import { IReactionDisposer, reaction } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import BlockedPeriodsToDateTree from 'modules/timeoff/lib/toDateTree/BlockedPeriods'
import CompanyHolidaysToDateTree from 'modules/timeoff/lib/toDateTree/CompanyHolidays'
import timeoffAllowances from 'modules/timeoff/store/collections/allowances'
import blockedPeriods from 'modules/timeoff/store/collections/blockedPeriods/blockedPeriods'
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import leaves from 'modules/timeoff/store/collections/leaves'
import timeoffPolicies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import { HALF_DAY_TYPES } from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import LeavesForm from './LeavesForm'

interface Props {
  form: LeaveForm
  readOnly?: boolean
  leaveTypeReadOnly?: boolean
  leaveId?: string
  leaveTypes: LeaveTypes
  year: number
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
}

const isHalfDaysEnabled = (selectedLeaveType: LeaveType | undefined): boolean => {
  if (!selectedLeaveType) return false

  return selectedLeaveType.attributes.half_days_units_enabled
}

const isInHours = (form: LeaveForm, selectedLeaveType: LeaveType | undefined): boolean => {
  if (!selectedLeaveType) return false

  const policyId = form.get('policy_id').value
  // Use the leave type's method to determine if it's hourly for this policy
  return selectedLeaveType.isHourlyForEmployee(String(policyId))
}

const allowancesForPolicy = (form: LeaveForm): Allowance[] => {
  const policyField = form.get('policy_id')

  return timeoffAllowances.filter(
    (allowance) => allowance.attributes.timeoff_policy_id === policyField.value
  )
}

const dayTypeOptions = (
  form: LeaveForm,
  selectedLeaveType: LeaveType | undefined
): SelectOption[] => {
  const options: SelectOption[] = []

  const inHours = isInHours(form, selectedLeaveType)

  if (inHours) {
    options.push({
      value: DAY_TYPES.hours,
      label: i18n.t('time_off.day_types.hours'),
    })

    if (hasFeature('dev_timeoff_forbid_asking_for_day_units')) {
      return options
    }
  }

  if (isHalfDaysEnabled(selectedLeaveType)) {
    options.push({
      value: DAY_TYPES.half_day,
      label: i18n.t('time_off.day_types.half_day'),
    })
  }

  options.push({
    value: DAY_TYPES.days,
    label: i18n.t('time_off.day_types.days'),
  })

  return options
}

const dayType = (form: LeaveForm, selectedLeaveType: LeaveType | undefined): string => {
  if (!isEmpty(form.get('half_day').value)) return DAY_TYPES.half_day

  if (
    form.get('hours_amount_in_cents').value ||
    (hasFeature('dev_timeoff_forbid_asking_for_day_units') && isInHours(form, selectedLeaveType))
  ) {
    return DAY_TYPES.hours
  }

  return DAY_TYPES.days
}

const BulkLeavesContent = ({
  form,
  readOnly = false,
  leaveTypeReadOnly = false,
  leaveTypes,
  year,
  leaveId,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
}: Props) => {
  useUnsafeEffect(() => {
    const currentPolicyId = form.get('policy_id').value
    if (currentPolicyId) return

    const mainPolicyId = timeoffPolicies.models.find((policy) => policy.attributes.main)?.attributes
      .id
    const fallbackPolicyId = timeoffPolicies.models[0]?.attributes.id
    const nextPolicyId = mainPolicyId ?? fallbackPolicyId

    if (nextPolicyId) {
      form.get('policy_id').set(nextPolicyId)
    }
  })

  const leaveTypeId = form.get('leave_type_id').value

  const selectedLeaveType = leaveTypes.get(leaveTypeId)

  const dayTypeForm = useLazyRef(
    () =>
      new LeaveDayTypeForm({
        day_type: dayType(form, selectedLeaveType),
      })
  ).current

  useAsyncData(async () => {
    if (!leaveTypeId) return

    const prevYear = moment([year - 1]).startOf('year')
    const nextYear = moment([year + 2]).endOf('year')

    leaves.fetch({
      data: {
        start_on: dateToString(prevYear, 'backend'),
        finish_on: dateToString(nextYear, 'backend'),
      },
    })

    return blockedPeriods.fetch({
      data: {
        leave_type_id: leaveTypeId,
        start_on: dateToString(prevYear, 'backend'),
        finish_on: dateToString(nextYear, 'backend'),
      },
    })
  }, [leaveTypeId])

  const companyHolidaysMuncher = useCallback(() => {
    return new CompanyHolidaysToDateTree(companyHolidays.toArray()).munch()
  }, [])

  const blockedPeriodsMuncher = useCallback(() => {
    return new BlockedPeriodsToDateTree(blockedPeriods.toArray()).munch(year)
  }, [year])

  const policyFieldOptions = timeoffPolicies.models.map((policy) => ({
    value: policy.get('id'),
    label: policy.get('name'),
  }))

  const allowanceForPolicyIds = allowancesForPolicy(form).map((allowance) => allowance.get('id'))

  const leaveTypesForPolicy = leaveTypes.filter(
    (leaveType) =>
      !!leaveType.attributes.active &&
      allowanceForPolicyIds.some((allowanceForPolicyId) =>
        leaveType.get('allowance_ids').includes(allowanceForPolicyId)
      )
  )

  useUnsafeEffect(() => {
    const dayTypeFormCurrent: Field = dayTypeForm.get('day_type')
    const dayTypeCurrent: string = dayType(form, selectedLeaveType)

    if (dayTypeFormCurrent.value !== dayTypeCurrent) {
      dayTypeFormCurrent.set(dayTypeCurrent)
    }

    // Sync form fields when day type changes (half_day, hours_amount_in_cents, start_time)
    let handlerDayType: IReactionDisposer | null = reaction(
      () => dayTypeForm.get('day_type').value,
      (reactionDayType: string) => {
        if (reactionDayType !== DAY_TYPES.half_day) {
          form.get('half_day').set('')
        } else {
          form.get('half_day').set(HALF_DAY_TYPES.beggining_of_day)
        }

        if (reactionDayType !== DAY_TYPES.hours) {
          form.get('hours_amount_in_cents').set('')
          form.get('start_time').set('')
        }
      }
    )

    // Refresh: When policy changes, validate leave type and reset day type
    // - Recalculates available leave types for new policy
    // - Clears leave type if invalid, selects first available if none
    // - Updates day type based on selected/available leave type
    let handlerPolicyId: IReactionDisposer | null = reaction(
      () => form.get('policy_id').value,
      (newPolicyId: string | number) => {
        const currentPolicyId = String(newPolicyId)
        const currentLeaveTypeId = form.get('leave_type_id').value

        const currentPolicyAllowanceIds = timeoffAllowances
          .filter((allowance) => String(allowance.attributes.timeoff_policy_id) === currentPolicyId)
          .map((allowance) => allowance.get('id'))

        const currentLeaveTypesForPolicy = leaveTypes.filter(
          (leaveType) =>
            !!leaveType.attributes.active &&
            currentPolicyAllowanceIds.some((allowanceId) =>
              leaveType.get('allowance_ids').includes(allowanceId)
            )
        )

        const currentLeaveTypeValid = currentLeaveTypeId
          ? currentLeaveTypesForPolicy.some((lt) => lt.get('id') === currentLeaveTypeId)
          : false

        let leaveTypeToUse: LeaveType | undefined
        if (currentLeaveTypeValid && currentLeaveTypeId) {
          leaveTypeToUse = leaveTypes.get(currentLeaveTypeId)
        } else {
          form.get('leave_type_id').set('')
          leaveTypeToUse = currentLeaveTypesForPolicy[0]
          if (leaveTypeToUse) {
            form.get('leave_type_id').set(leaveTypeToUse.get('id'))
          }
        }

        const newDayType = leaveTypeToUse ? dayType(form, leaveTypeToUse) : DAY_TYPES.days
        dayTypeForm.get('day_type').set(newDayType)
      }
    )

    // Refresh: When leave type changes, update day type and clear invalid fields
    // - Clears half_day if new leave type doesn't support it
    // - Calculates default day type and ensures it's available in options
    // - Falls back to first available option if calculated type is invalid
    let handlerLeaveTypeId: IReactionDisposer | null = reaction(
      () => form.get('leave_type_id').value,
      (newLeaveTypeId: string | number) => {
        const currentLeaveTypeId = String(newLeaveTypeId)
        const currentSelectedLeaveType = leaveTypes.get(currentLeaveTypeId)

        if (!currentSelectedLeaveType) {
          dayTypeForm.get('day_type').set(DAY_TYPES.days)
          return
        }

        if (!isHalfDaysEnabled(currentSelectedLeaveType)) {
          form.get('half_day').set('')
        }

        const availableOptions = dayTypeOptions(form, currentSelectedLeaveType)
        let reactionDayType = dayType(form, currentSelectedLeaveType)

        const isDayTypeAvailable = availableOptions.some(
          (option) => option.value === reactionDayType
        )

        if (!isDayTypeAvailable) {
          reactionDayType =
            availableOptions.length > 0
              ? (availableOptions[0]?.value ?? DAY_TYPES.days)
              : DAY_TYPES.days
        }

        dayTypeForm.get('day_type').set(reactionDayType)
      }
    )

    return () => {
      if (handlerDayType) {
        handlerDayType()
        handlerDayType = null
      }

      if (handlerPolicyId) {
        handlerPolicyId()
        handlerPolicyId = null
      }

      if (handlerLeaveTypeId) {
        handlerLeaveTypeId()
        handlerLeaveTypeId = null
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dayTypeForm, leaveTypeId])

  const computedDayTypeOptions = dayTypeOptions(form, selectedLeaveType)

  return (
    <LeavesForm
      readOnly={readOnly}
      leaveTypeReadOnly={leaveTypeReadOnly}
      form={form}
      dayTypeForm={dayTypeForm}
      dayTypeOptions={computedDayTypeOptions}
      companyHolidaysMuncher={companyHolidaysMuncher}
      blockedPeriodsMuncher={blockedPeriodsMuncher}
      selectedLeaveType={selectedLeaveType}
      timeTaken={timeTaken}
      labourUnits={labourUnits}
      isReviewOrEditMode={Boolean(isReviewOrEditMode)}
      policyFieldOptions={policyFieldOptions}
      leaveTypesForPolicy={leaveTypesForPolicy}
      leaveId={leaveId}
      leaveTypeId={leaveTypeId}
    />
  )
}

export default observer(BulkLeavesContent)
